import { createSlice, createSelector } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { DefaultFailureAction, DefaultSuccessAction, dataHookFactory } from "../common/reducers/utilities.js";
import { ensureArray } from "../common/utils/arrayUtilities.js";
import { markFetched } from "../common/hooks/initHook.js";

const key = q => q.id;

export const qualificationSlice = createSlice({
    name: 'qualifications',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, q) => { obj[key(q)] = q; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(q => state.data[key(q)] = q);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(q => delete state.data[key(q)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => qualificationSlice.getInitialState());
    }
})

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'qualification_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'qualification_one');

const fetchAllQualifications = network => network.get('/api/v1/qualifications', qualificationSlice.actions.set, visualizeFailure('get'))

export const createQualification = (network, qual) => network.post('/api/v1/qualification', qual, qualificationSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateQualification = (network, delta) => network.put('/api/v1/qualification', delta, qualificationSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteQualification = (network, id) => network.delete('/api/v1/qualification/' + window.encodeURIComponent(id), qualificationSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectQualificationData = state => state.qualifications.data;
const selectQualifications = createSelector([selectQualificationData], data => Object.values(data))
const selectQualification = id => createSelector([selectQualificationData], quals => quals[id])
// #endregion

const listen = {
    qualificationCreated: (qual, { dispatch, websocket }) => {
        markFetched(websocket, 'qualification', key(qual))
        dispatch(qualificationSlice.actions.addOrUpdate(qual))
    },
    qualificationUpdated: (qual, { dispatch }) => dispatch(qualificationSlice.actions.addOrUpdate(qual)),
    qualificationRemoved: (qual, { dispatch }) => dispatch(qualificationSlice.actions.remove(qual))
}
const initQualifications = { run: fetchAllQualifications, listen, immortal: true }
export const [useQualifications] = dataHookFactory(selectQualifications, initQualifications);
export const [useQualification, realizeQualification] = dataHookFactory(selectQualification, id => initQualifications);

const fullRoles = ['administrator', 'training manager'];
const readonlyRoles = fullRoles.concat(['auditor', 'training auditor', 'member manager', 'member auditor']);
export const canUpdateQualifications = (credentials) => credentials.hasRole(fullRoles);
export const canCreateQualifications = canUpdateQualifications;
export const canRemoveQualifications = canUpdateQualifications;
export const hideQualifications = (credentials, member) => !(credentials.hasRole(fullRoles.concat(readonlyRoles)) || credentials.memberID == member.id);

export const canRouteQualifications = ({credentials}) => credentials.hasRole(fullRoles.concat(readonlyRoles));

const reducer = {name: qualificationSlice.name, reducer: qualificationSlice.reducer }
export default reducer;